import { Flex, Input, Menu, Spin, Typography } from 'antd';
import { Dropdown } from '@/components/AntdCompatDropdown';
import { DownOutlined } from '@ant-design/icons';
import { useState } from 'react';
import CustomShipmentText from './CustomShipmentText';
import {
  CloseCircleIcon,
  CloseDrawerIcon,
  PlusCircleIcon,
} from '@/assets/icons/icons';

const scrollContainerStyle = {
  maxHeight: '300px',
  overflowY: 'auto',
};
const optionStyle = {
  backgroundColor: 'var(--surface-container)',
};

const optionRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  gap: '8px',
};

const optionTextStyle = {
  flex: 1,
  minWidth: 0,
};

const deleteIconWrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'pointer',
};

const addTextStyle = {
  marginLeft: '8px',
};

const inputStyle = {
  flex: 1,
  border: 'none',
  height: '28px',
  padding: '0px',
};

const closeInputIconStyle = {
  background: 'var(--surface-container)',
};

const spinnerWrapperStyle = {
  width: '100%',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const dropdownTriggerStyle = (disabled, dropdownStyle) => ({
  width: '100%',
  height: '36px',
  backgroundColor: 'var(--light-gray)',
  borderRadius: '8px',
  border: 'none',
  padding: '8px 12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: disabled ? '#ccc' : 'var(--text-secondary)',
  opacity: disabled ? 0.6 : 1,
  ...dropdownStyle,
});

const DropdownWithInput = ({
  options = [],
  value,
  onSelect,
  onAdd,
  onDeleteOption,
  placeholder = 'Select...',
  addText = 'Add',
  inputPlaceholder = 'Enter new item',
  dropdownStyle = {},
  disabled = false,
  ...props
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingLoading, setIsAddingLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const handleAdd = async () => {
    if (inputValue.trim()) {
      try {
        setIsAddingLoading(true);
        await onAdd(inputValue.trim());
        setInputValue('');
        setIsAdding(false);
      } catch (error) {
        console.error('Error adding item:', error);
      } finally {
        setIsAddingLoading(false);
      }
    }
  };

  const handleSelect = (selectedValue) => {
    onSelect(selectedValue);
    setDropdownVisible(false);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setInputValue('');
    // setDropdownVisible(false);
    setIsAddingLoading(false);
  };

  const getDisplayText = () => {
    if (!value) return placeholder;
    const selectedOption = options.find((option) => option.id === value);
    return selectedOption?.name || placeholder;
  };

  const menu = (
    <Menu>
      <div style={scrollContainerStyle}>
        {options.map((option) => (
          <Menu.Item
            className='dropdown-menu-item'
            key={option.id}
            style={optionStyle}
            onClick={() => handleSelect(option.id)}
          >
            <div style={optionRowStyle}>
              <Typography.Text
                style={optionTextStyle}
                ellipsis={{
                  tooltip: option.name,
                }}
              >
                {option.name}
              </Typography.Text>

              {option.can_delete !== false && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDeleteOption?.(option);
                  }}
                  style={deleteIconWrapperStyle}
                >
                  <CloseDrawerIcon
                    width={16}
                    height={16}
                    color='var(--zl-icon-muted, #00000073)'
                  />
                </span>
              )}
            </div>
          </Menu.Item>
        ))}
      </div>

      <Menu.Divider />

      {!isAdding ? (
        <Flex
          align='center'
          className='add-location-type-btn'
          onClick={(e) => {
            e.stopPropagation();
            setIsAdding(true);
          }}
        >
          <PlusCircleIcon />
          <CustomShipmentText
            text={addText}
            fontWeight={600}
            style={addTextStyle}
          />
        </Flex>
      ) : (
        <Menu.Item>
          {isAddingLoading ? (
            <div
              style={spinnerWrapperStyle}
              onClick={(e) => e.stopPropagation()}
            >
              <Spin size='small' />
            </div>
          ) : (
            <>
              <div className='download-report-editor-input custom-no-border-input'>
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={inputPlaceholder}
                  style={inputStyle}
                  size='small'
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.stopPropagation();
                      handleAdd();
                    } else if (e.key === 'Escape') {
                      handleCancel();
                    }
                  }}
                />
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancel();
                  }}
                >
                  <CloseCircleIcon
                    style={closeInputIconStyle}
                    className='close-input-icon'
                  />
                </div>
              </div>
            </>
          )}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown
      visible={dropdownVisible}
      onVisibleChange={(visible) => {
        setDropdownVisible(visible);
        if (!visible) {
          setIsAdding(false);
          setInputValue('');
          setIsAddingLoading(false);
        }
      }}
      overlay={menu}
      trigger={['click']}
      // placement='bottomLeft'
      disabled={disabled}
      styles={{
        root: {
          width: 200,
        },
      }}
    >
      <div style={dropdownTriggerStyle(disabled, dropdownStyle)} {...props}>
        <Typography.Text
          style={optionTextStyle}
          ellipsis={{
            tooltip: getDisplayText(),
          }}
        >
          {getDisplayText()}
        </Typography.Text>
        {!disabled && <DownOutlined />}
      </div>
    </Dropdown>
  );
};

export default DropdownWithInput;
